
import { Grid2X2, List } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResumeCard from "../ResumeCard";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ResumeListProps {
  resumes: any[];
  viewMode: "grid" | "list";
  onViewModeChange: (value: "grid" | "list") => void;
  onPreview: (resume: any) => void;
  onDownload: (resume: any) => void;
  onDelete: (resume: any) => void;
  loading?: boolean; // Added loading prop as optional
}

const NoResumesFound = ({
  onClearFilters,
}: {
  onClearFilters: () => void;
}) => (
  <div className="text-center p-12 bg-gray-50 rounded-lg border border-gray-100">
    <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium">No matching resumes</h3>
    <p className="text-muted-foreground mt-2">
      Try adjusting your search or filters
    </p>
    <Button variant="outline" className="mt-4" onClick={onClearFilters}>
      Clear filters
    </Button>
  </div>
);

const ResumeList = ({
  resumes,
  viewMode,
  onViewModeChange,
  onPreview,
  onDownload,
  onDelete,
  loading = false, // Add default value for loading
}: ResumeListProps) => {
  if (resumes.length === 0) {
    return <NoResumesFound onClearFilters={() => onViewModeChange("grid")} />;
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Tabs value={viewMode} onValueChange={(v) => onViewModeChange(v as "grid" | "list")} className="hidden sm:block">
          <TabsList className="h-9">
            <TabsTrigger value="grid" className="px-3">
              <Grid2X2 className="h-4 w-4" />
            </TabsTrigger>
            <TabsTrigger value="list" className="px-3">
              <List className="h-4 w-4" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <div className={viewMode === "grid" 
        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
        : "flex flex-col gap-4"
      }>
        {loading ? (
          // Show loading indicators when loading is true
          Array(3).fill(0).map((_, index) => (
            <div key={index} className="border rounded-md p-4 h-48 animate-pulse bg-gray-50">
              <div className="h-5 w-3/4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 w-1/2 bg-gray-200 rounded mb-6"></div>
              <div className="flex gap-2 mb-3">
                <div className="h-5 w-16 bg-gray-200 rounded"></div>
                <div className="h-5 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="flex justify-between mt-6">
                <div className="h-7 w-20 bg-gray-200 rounded"></div>
                <div className="h-7 w-20 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          resumes.map((resume) => (
            <ResumeCard
              key={resume.id}
              resume={resume}
              onPreview={onPreview}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </>
  );
};

export default ResumeList;
